import React from 'react';
import clsx from 'clsx';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import KeyShortcut from './KeyShortcut';
import { useDialogFullScreen } from '@erikdakoda/ui/hooks/useDialogFullScreen';

export default function ConfirmationDialog({
  open,
  options,
  onCancel,
  onConfirm,
  onSecondary,
  onClose,
}) {
  'use memo';

  const fullScreen = useDialogFullScreen();

  const {
    title,
    description,
    content,
    confirmationText,
    secondaryText,
    cancellationText,
    dialogProps,
    dialogActionsProps,
    confirmationButtonProps,
    secondaryButtonProps,
    cancellationButtonProps,
    titleProps,
    contentProps,
    allowClose,
    confirmationKeyword,
    confirmationKeywordTextFieldProps,
    hideCancelButton,
    showSecondaryButton,
    buttonOrder,
    acknowledgement,
    acknowledgementFormControlLabelProps,
    acknowledgementCheckboxProps,
  } = options;

  const [confirmationKeywordValue, setConfirmationKeywordValue] = React.useState('');
  const [isAcknowledged, setIsAcknowledged] = React.useState(false);

  const confirmationButtonDisabled = Boolean(
    (confirmationKeyword && confirmationKeywordValue !== confirmationKeyword) ||
    (acknowledgement && !isAcknowledged),
  );

  const acknowledgeCheckbox = acknowledgement ? (
    <FormControlLabel
      {...acknowledgementFormControlLabelProps}
      control={
        <Checkbox
          {...acknowledgementCheckboxProps}
          value={isAcknowledged}
          onChange={(_, value) => setIsAcknowledged(value)}
        />
      }
      label={acknowledgement}
    />
  ) : null;

  const confirmationContent = confirmationKeyword ? (
    <TextField
      onChange={(e) => setConfirmationKeywordValue(e.target.value)}
      value={confirmationKeywordValue}
      fullWidth
      {...confirmationKeywordTextFieldProps}
    />
  ) : null;

  const dialogActions = buttonOrder.map((buttonType) => {
    if (buttonType === 'cancel') {
      return (
        !hideCancelButton && (
          <Button key="cancel" {...cancellationButtonProps} onClick={onCancel}>
            {cancellationText} <KeyShortcut eventKey="Escape" />
          </Button>
        )
      );
    }

    if (buttonType === 'confirm') {
      return (
        <Button
          key="confirm"
          color="primary"
          disabled={confirmationButtonDisabled}
          {...confirmationButtonProps}
          onClick={onConfirm}
        >
          {confirmationText} <KeyShortcut eventKey="Enter" />
        </Button>
      );
    }

    if (buttonType === 'secondary') {
      return (
        showSecondaryButton && (
          <Button key="secondary" {...secondaryButtonProps} onClick={onSecondary}>
            {secondaryText}
          </Button>
        )
      );
    }
    throw new Error(
      `Supported button types are only "confirm", "secondary", and "cancel", got: ${buttonType}`,
    );
  });

  const handleShortcutKeys = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onConfirm();
      event.preventDefault();
    }
  };

  /* useKeyboardEvents({
    ignoreUsedKeyboardEvents: false,
    onKeyDown: handleShortcutKeys,
  }); */

  return (
    <Dialog
      fullWidth
      fullScreen={fullScreen}
      {...dialogProps}
      className={clsx(dialogProps?.className, 'confirmation-dialog')}
      open={open}
      onClose={allowClose ? onClose : null}
      onKeyDown={handleShortcutKeys}
    >
      {title && <DialogTitle {...titleProps}>{title}</DialogTitle>}
      <DialogContent {...contentProps}>
        {description ? <DialogContentText component="div">{description}</DialogContentText> : null}
        {content}
        {confirmationContent}
        {acknowledgeCheckbox}
      </DialogContent>
      <DialogActions {...dialogActionsProps}>{dialogActions}</DialogActions>
    </Dialog>
  );
}
